import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  Button,
  Header,
  Link,
  Modal,
  Select,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
  Toggle,
} from '@amzn/awsui-components-console';
import { SelectProps } from '@amzn/awsui-components-console/select/interfaces';
import {
  DeleteGroupCommand,
  GroupSummary,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { mapWorkStatusToReadableString } from '../ConferencePages/conferenceInfo/utils';
import { WorkStatusBadge } from '../ConferencePages/conferenceInfo/ui/WorkStatusBadge';
import { EmptyState } from '../ConferencePages/ui/EmptyState';
import { CreateGroupModal } from './CreateGroupModal';
import { useAuthState } from '../authentication/context';
import { useApiErrors } from '../components/ApiErrorContext';
import { callApi } from '../client';
import { matchesGroupFilters } from '../utils/statusFilters';
import { AskFusionAssistantButton } from '../fusionAssistant/AskFusionAssistantButton';

interface GroupListTableProps {
  data: GroupSummary[];
  loading: boolean;
  onRefresh: () => void;
  // Display-only: maps (conference, venue) to the coordinator's canonicalVenueName, falling back to
  // the raw venue (tcx-core-platforms-903 D2).
  venueDisplayName?: (conference?: string, venue?: string) => string;
}

const COLUMN_MIN_WIDTH = 176;

export const GroupListTable = ({
  data,
  loading,
  onRefresh,
  venueDisplayName,
}: GroupListTableProps) => {
  const [selectedItems, setSelectedItems] = useState<GroupSummary[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Single-select workStatus view (mutually exclusive): groups can only be RESOLVED or not, so the
  // view is Active vs Resolved. Archived is a separate orthogonal toggle. tcx-core-platforms-903.
  const [statusView, setStatusView] = useState<'default' | 'resolved'>(
    'default'
  );
  const [showArchived, setShowArchived] = useState(false);
  const { token } = useAuthState();
  const { pushError } = useApiErrors();
  const history = useHistory();

  const columns = [
    {
      id: 'groupName',
      header: 'Group name',
      cell: (g: GroupSummary) =>
        g.groupName ? (
          <Link href={`#/groups/view-${g.groupId}`}>{g.groupName}</Link>
        ) : (
          '-'
        ),
      sortingField: 'groupName',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'groupId',
      header: 'Group ID',
      cell: (g: GroupSummary) => (
        <span style={{ fontFamily: 'monospace' }}>{g.groupId}</span>
      ),
      sortingField: 'groupId',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'conference',
      header: 'Conference',
      cell: (g: GroupSummary) => g.conference || '-',
      sortingField: 'conference',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'venue',
      header: 'Venue',
      // Show the coordinator's canonical name when set, else the raw venue (display-only). D2.
      cell: (g: GroupSummary) =>
        venueDisplayName
          ? venueDisplayName(g.conference, g.venue)
          : g.venue || '-',
      sortingField: 'venue',
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'memberTasks',
      header: 'Member tasks',
      cell: (g: GroupSummary) => g.memberTaskIds?.length ?? 0,
      sortingField: 'memberTasks',
      sortingComparator: (a: GroupSummary, b: GroupSummary) =>
        (a.memberTaskIds?.length ?? 0) - (b.memberTaskIds?.length ?? 0),
      minWidth: COLUMN_MIN_WIDTH,
    },
    {
      id: 'workStatus',
      header: 'Status',
      cell: (g: GroupSummary) =>
        g.workStatus ? (
          <WorkStatusBadge status={g.workStatus} showPrefix={false} />
        ) : (
          '-'
        ),
      sortingField: 'workStatus',
      minWidth: COLUMN_MIN_WIDTH,
    },
  ];

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(data || [], {
    filtering: {
      empty: (
        <EmptyState
          title='No groups'
          subtitle='No groups have been created yet.'
          action={<></>}
        />
      ),
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='No groups matched the specified filter'
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Clear filter
            </Button>
          }
        />
      ),
      filteringFunction: (group, filteringText) => {
        if (!group) return false;
        // Two orthogonal, AND-joined hidden dimensions (tcx-core-platforms-903): the single-select
        // statusView (Active vs Resolved) and the Archived toggle. Delegated to the pure
        // matchesGroupFilters helper (unit-tested in utils/statusFilters.test.ts).
        if (
          !matchesGroupFilters({
            workStatus: group.workStatus,
            archived: group.archived,
            view: statusView,
            showArchived,
          })
        ) {
          return false;
        }
        const text = filteringText.toLowerCase();
        const translatedStatus = group.workStatus
          ? mapWorkStatusToReadableString(group.workStatus)?.toLowerCase()
          : '';
        // Match the venue on BOTH the raw venue and the coordinator's canonical display name, so a
        // user who sees "Alpha (Main Stage)" in the Venue column can filter by it. The column renders
        // venueDisplayName, so filtering only on the raw venue would silently return no matches for a
        // canonical-name query (tcx-core-platforms-903 D2).
        const displayVenue = venueDisplayName
          ? venueDisplayName(group.conference, group.venue).toLowerCase()
          : '';
        return (
          (group.groupName?.toLowerCase().includes(text) ?? false) ||
          (group.groupId?.toLowerCase().includes(text) ?? false) ||
          (group.venue?.toLowerCase().includes(text) ?? false) ||
          (displayVenue.includes(text) ?? false) ||
          (group.conference?.toLowerCase().includes(text) ?? false) ||
          (group.workStatus?.toLowerCase().includes(text) ?? false) ||
          (translatedStatus?.includes(text) ?? false) ||
          (group.memberTaskIds?.some((id) => id.toLowerCase().includes(text)) ??
            false)
        );
      },
    },
    pagination: { pageSize: 20 },
    sorting: {
      defaultState: {
        sortingColumn: columns[0],
        isDescending: false,
      },
    },
    selection: {},
  });

  const handleDelete = async () => {
    if (!token) return;
    setDeleting(true);

    for (const group of selectedItems) {
      try {
        await callApi(
          new DeleteGroupCommand({ groupId: group.groupId }),
          token
        );
      } catch (err: any) {
        pushError(
          'Delete Group Failed',
          `Failed to delete "${group.groupName}": ${
            err?.message || String(err)
          }`
        );
      }
    }

    setDeleting(false);
    setDeleteModalVisible(false);
    setSelectedItems([]);
    onRefresh();
  };

  const renderDeleteModalContent = () => {
    if (selectedItems.length === 1) {
      const group = selectedItems[0];
      return (
        <Box>
          Are you sure you want to delete <b>{group.groupName}</b>? This will
          permanently delete the group and reset all{' '}
          <b>{group.memberTaskIds?.length ?? 0} member task(s)</b> to{' '}
          <b>Task Received</b> status, clearing their doc packages. This action
          cannot be undone.
        </Box>
      );
    }
    return (
      <SpaceBetween size='s'>
        <Box>
          Are you sure you want to delete the following {selectedItems.length}{' '}
          groups? All member tasks will be reset to <b>Task Received</b> status
          and their doc packages will be cleared. This action cannot be undone.
        </Box>
        <ul>
          {selectedItems.map((g) => (
            <li key={g.groupId}>
              {g.groupName} ({g.memberTaskIds?.length ?? 0} member tasks)
            </li>
          ))}
        </ul>
      </SpaceBetween>
    );
  };

  // Single-select workStatus view (mutually exclusive). Archived is a separate orthogonal toggle
  // rendered alongside it. tcx-core-platforms-903.
  const STATUS_VIEW_OPTIONS: SelectProps.Option[] = [
    { label: 'Active groups', value: 'default' },
    { label: 'Resolved groups', value: 'resolved' },
  ];
  const selectedStatusViewOption: SelectProps.Option =
    STATUS_VIEW_OPTIONS.find((o) => o.value === statusView) ??
    STATUS_VIEW_OPTIONS[0];

  return (
    <Box margin={{ top: 'l', bottom: 'xxl' }}>
      <Table
        {...collectionProps}
        resizableColumns
        onSelectionChange={({ detail }) =>
          setSelectedItems(detail.selectedItems)
        }
        selectedItems={selectedItems}
        columnDefinitions={columns}
        items={items}
        loadingText='Retrieving groups'
        loading={loading}
        selectionType='multi'
        trackBy='groupId'
        stickyHeader={true}
        header={
          <Header
            actions={
              <SpaceBetween direction='horizontal' size='s' alignItems='center'>
                {/* Delete — visible when ≥1 row selected */}
                <Button
                  disabled={selectedItems.length === 0}
                  onClick={() => setDeleteModalVisible(true)}
                  data-test-id='delete-group-button'
                >
                  Delete
                </Button>
                {/* View details — visible when exactly 1 row selected */}
                <Button
                  disabled={selectedItems.length !== 1}
                  variant='primary'
                  href={
                    selectedItems.length === 1
                      ? `#/groups/view-${selectedItems[0].groupId}`
                      : undefined
                  }
                  data-test-id='view-group-details-button'
                >
                  View details
                </Button>
                {/* Create group — always visible */}
                <Button
                  variant='primary'
                  onClick={() => setCreateModalVisible(true)}
                  data-test-id='create-group-button'
                >
                  Create group
                </Button>
                {/* Ask Fusion Assistant — always visible */}
                <AskFusionAssistantButton />
              </SpaceBetween>
            }
            counter={
              data && filteredItemsCount
                ? `(${filteredItemsCount}/${data.length})`
                : `(${items.length})`
            }
          >
            Groups
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <SpaceBetween direction='horizontal' size='xs'>
            <TextFilter
              {...filterProps}
              filteringPlaceholder='Filter by group name, ID, venue, conference, status, or member task'
            />
            <Select
              selectedOption={selectedStatusViewOption}
              onChange={({ detail }) =>
                setStatusView(
                  (detail.selectedOption.value as 'default' | 'resolved') ??
                    'default'
                )
              }
              options={STATUS_VIEW_OPTIONS}
              data-test-id='status-view-select'
            />
            <Toggle
              checked={showArchived}
              onChange={({ detail }) => setShowArchived(detail.checked)}
              data-test-id='archived-toggle'
            >
              Archived
            </Toggle>
          </SpaceBetween>
        }
      />

      {/* Delete confirmation modal */}
      <Modal
        visible={deleteModalVisible}
        onDismiss={() => setDeleteModalVisible(false)}
        header={
          selectedItems.length === 1
            ? 'Delete group?'
            : `Delete ${selectedItems.length} groups?`
        }
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => setDeleteModalVisible(false)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleDelete}
                loading={deleting}
                data-test-id='confirm-delete-group-button'
              >
                {selectedItems.length === 1 ? 'Delete' : 'Delete all'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {renderDeleteModalContent()}
      </Modal>

      <CreateGroupModal
        visible={createModalVisible}
        onDismiss={() => setCreateModalVisible(false)}
        onSuccess={(groupId) => {
          setCreateModalVisible(false);
          history.push(`/groups/view-${groupId}`);
        }}
      />
    </Box>
  );
};
